/**
 * aligned's `kdBlacklistedCountries` / `kdBlacklistedCountriesFullNames` — the
 * global sanctioned/unsupported-operations country list applied to every
 * customer. Per-customer blacklists (`Customer.blacklistedCountryCodes`) layer
 * on top via union: this list is the floor and cannot be overridden.
 *
 * Both representations are kept because the two sides of a comparison differ:
 * `PrivateInfo.addressCountry` stores a country *name*, while customer
 * blacklists store ISO *codes*. Use `isBlacklistedCountryName` rather than
 * comparing the two directly.
 */

/** ISO 3166-1 alpha-2 codes, in upstream's order. */
export const BLACKLISTED_COUNTRY_CODES: string[] = [
  'CU',
  'SD',
  'AF',
  'BY',
  'CF',
  'CD',
  'CN',
  'ET',
  'ER',
  'HT',
  'HK',
  'IR',
  'IQ',
  'LB',
  'MO',
  'MM',
  'NI',
  'NG',
  'KE',
  'PK',
  'PS',
  'RU',
  'SO',
  'SS',
  'SY',
  'VE',
  'YE',
];

/** The same countries as stored in `PrivateInfo.addressCountry` (display names). */
export const BLACKLISTED_COUNTRY_NAMES: string[] = [
  'Afghanistan',
  'Belarus',
  'Central African Republic',
  'China',
  'Congo, Democratic Republic of the',
  'Cuba',
  'Eritrea',
  'Ethiopia',
  'Haiti',
  'Hong Kong',
  'Iran',
  'Iraq',
  'Kenya',
  'Lebanon',
  'Macau',
  'Myanmar',
  'Nicaragua',
  'Nigeria',
  'Pakistan',
  'Palestine',
  'Russia',
  'Somalia',
  'South Sudan',
  'Sudan',
  'Syria',
  'Venezuela',
  'Yemen',
];

/** Maps the blacklisted display names to their ISO codes, for name→code lookups. */
const CODE_BY_NAME = new Map<string, string>([
  ['afghanistan', 'AF'],
  ['belarus', 'BY'],
  ['central african republic', 'CF'],
  ['china', 'CN'],
  ['congo, democratic republic of the', 'CD'],
  ['cuba', 'CU'],
  ['eritrea', 'ER'],
  ['ethiopia', 'ET'],
  ['haiti', 'HT'],
  ['hong kong', 'HK'],
  ['iran', 'IR'],
  ['iraq', 'IQ'],
  ['kenya', 'KE'],
  ['lebanon', 'LB'],
  ['macau', 'MO'],
  ['myanmar', 'MM'],
  ['nicaragua', 'NI'],
  ['nigeria', 'NG'],
  ['pakistan', 'PK'],
  ['palestine', 'PS'],
  ['russia', 'RU'],
  ['somalia', 'SO'],
  ['south sudan', 'SS'],
  ['sudan', 'SD'],
  ['syria', 'SY'],
  ['venezuela', 'VE'],
  ['yemen', 'YE'],
]);

/** ISO code for a stored country name, or null when it is not a blacklisted country. */
export function countryCodeForName(countryName: string | null | undefined): string | null {
  if (!countryName) return null;
  return CODE_BY_NAME.get(countryName.trim().toLocaleLowerCase()) ?? null;
}

/**
 * True when a stored `addressCountry` name falls on the global blacklist or on
 * the supplied per-customer code list. Callers decide separately how to treat a
 * missing country.
 */
export function isBlacklistedCountryName(
  countryName: string | null | undefined,
  customerBlacklistedCodes: string[] = [],
): boolean {
  if (!countryName) return false;
  const normalizedName = countryName.trim().toLocaleLowerCase();
  const code = countryCodeForName(countryName);
  if (code !== null && BLACKLISTED_COUNTRY_CODES.includes(code)) return true;

  const displayNames =
    typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
      ? new Intl.DisplayNames(['en'], { type: 'region' })
      : null;
  return customerBlacklistedCodes.some((customerCode) => {
    const normalizedCode = customerCode.trim().toLocaleUpperCase();
    if (!normalizedCode) return false;
    try {
      return displayNames?.of(normalizedCode)?.trim().toLocaleLowerCase() === normalizedName;
    } catch {
      return false;
    }
  });
}
